import { File, Paths } from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { useShallow } from 'zustand/shallow';

import { Stack } from '@/components/layout';
import { showAlert, WebView } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { CoachMaterialHeader } from '@/features/guardian-player/components/coach-material-header';
import { createStyles, useScaling } from '@/hooks';
import type { GuardianStackScreenProps } from '@/navigations/types';
import { useUiStore } from '@/stores';
import { colors } from '@/theme/colors';

type Props = GuardianStackScreenProps<'GuardianCoachMaterialWebView'>;

function derivePdfFileName(url: string): string {
  let base = '';
  const nameMatch = url.match(/[?&]name=([^&]+)/i);
  if (nameMatch) {
    try {
      base = decodeURIComponent(nameMatch[1].replace(/\+/g, ' '));
    } catch {
      base = nameMatch[1];
    }
  }
  if (!base) {
    const path = url.split(/[?#]/)[0];
    base = path.substring(path.lastIndexOf('/') + 1);
  }
  base = base.replace(/[\\/:*?"<>|]+/g, '_').trim();
  if (!base) base = 'download';
  if (!/\.pdf$/i.test(base)) base += '.pdf';
  return base;
}

export function GuardianCoachMaterialWebViewScreen({ navigation, route }: Props) {
  const styles = useStyles();
  const { isTablet, deviceWidth, deviceHeight } = useScaling();
  const [loading, setLoading] = useState(false);
  const didFinishInitialLoadRef = useRef(false);
  const { url, title } = route.params;
  const isPortrait = deviceHeight >= deviceWidth;

  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));

  const handleFileDownload = useCallback(
    async (downloadUrl: string) => {
      const targetUrl = downloadUrl?.trim();
      if (!targetUrl) return;
      showGlobalLoading();
      try {
        const destination = new File(Paths.cache, derivePdfFileName(targetUrl));
        const file = await File.downloadFileAsync(targetUrl, destination, { idempotent: true });
        if (await Sharing.isAvailableAsync()) {
          await Sharing.shareAsync(file.uri, {
            mimeType: 'application/pdf',
            UTI: 'com.adobe.pdf',
          });
        }
      } catch {
        showAlert({
          title: STRINGS.common.playerPathwayScreen.download.errorTitle,
          message: STRINGS.common.playerPathwayScreen.download.errorMessage,
        });
      } finally {
        hideGlobalLoading();
      }
    },
    [hideGlobalLoading, showGlobalLoading],
  );

  useEffect(() => {
    didFinishInitialLoadRef.current = false;
    setLoading(false);
  }, [url]);

  return (
    <Stack flex={1} backgroundColor="#141A26">
      <CoachMaterialHeader title={title} onBack={() => navigation.goBack()} />

      <Stack flex={1} style={styles.webviewContainer}>
        {loading ? (
          <Stack style={styles.loadingOverlay} pointerEvents="none">
            <ActivityIndicator size="large" color={colors.Blue} />
          </Stack>
        ) : null}

        <WebView
          source={{ uri: url }}
          style={styles.webview}
          isTablet={isTablet}
          forceMobileOnTablet
          forceMobileViewportWidth={isTablet && isPortrait ? 430 : undefined}
          onFileDownload={({ nativeEvent }) => handleFileDownload(nativeEvent.downloadUrl)}
          onLoadStart={() => {
            if (!didFinishInitialLoadRef.current) setLoading(true);
          }}
          onLoadEnd={() => {
            didFinishInitialLoadRef.current = true;
            setLoading(false);
          }}
          onError={() => {
            didFinishInitialLoadRef.current = true;
            setLoading(false);
          }}
        />
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(() => ({
  webviewContainer: {
    flex: 1,
    backgroundColor: '#141A26',
  },
  webview: {
    flex: 1,
    zIndex: 9,
    backgroundColor: 'transparent',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
}));
